import { useState, useEffect, useCallback } from 'react';
import { useAccessToken } from './useAuth';
import { useAlert } from '../context/AlertContext';
import { notePriorityService } from '../services/notePriorityService';

/**
 * Custom hook for managing note priorities
 * @param {Object} options - Configuration options
 * @param {string} options.order - Sort order ('asc' or 'desc')
 * @param {boolean} options.autoFetch - Automatically fetch data on mount
 * @returns {Object} Hook return value
 */
export const useNotePriorities = (options = {}) => {
    const { order = 'desc', autoFetch = true } = options;
    const { getBackendAccessToken } = useAccessToken();
    const { setErrorAlert, setInfoAlert } = useAlert();
    
    const [priorities, setPriorities] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Fetch note priorities
    const fetchPriorities = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            
            const accessToken = await getBackendAccessToken();
            const data = await notePriorityService.getAll(accessToken, order);

            setPriorities(data);
            return data;
        } catch (err) {
            console.error('Error fetching note priorities:', err);
            const errorMessage = err.message || 'Failed to load note priorities';
            setError(errorMessage);
            setErrorAlert(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [order, getBackendAccessToken, setErrorAlert]);

    // Create note priority
    const createPriority = useCallback(async (priorityData) => {
        try {
            setLoading(true);
            setError(null);
            
            const accessToken = await getBackendAccessToken();
            const newPriority = await notePriorityService.create(accessToken, priorityData);
            setInfoAlert('Note priority created successfully');
            
            // Refresh the list
            await fetchPriorities();
            
            return newPriority;
        } catch (err) {
            console.error('Error creating note priority:', err);
            const errorMessage = err.message || 'Failed to create note priority';
            setError(errorMessage);
            setErrorAlert(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [getBackendAccessToken, fetchPriorities, setInfoAlert, setErrorAlert]);

    // Update note priority
    const updatePriority = useCallback(async (id, priorityData) => {
        try {
            setLoading(true);
            setError(null);
            
            const accessToken = await getBackendAccessToken();
            const updatedPriority = await notePriorityService.update(accessToken, id, priorityData);
            setInfoAlert('Note priority updated successfully');
            
            // Refresh the list
            await fetchPriorities();
            
            return updatedPriority;
        } catch (err) {
            console.error('Error updating note priority:', err);
            const errorMessage = err.message || 'Failed to update note priority';
            setError(errorMessage);
            setErrorAlert(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [getBackendAccessToken, fetchPriorities, setInfoAlert, setErrorAlert]);

    // Delete note priority
    const deletePriority = useCallback(async (id) => {
        try {
            setLoading(true);
            setError(null);
            
            const accessToken = await getBackendAccessToken();
            const result = await notePriorityService.delete(accessToken, id);
            setInfoAlert('Note priority deleted successfully');
            
            // Refresh the list
            await fetchPriorities();
            
            return result;
        } catch (err) {
            console.error('Error deleting note priority:', err);
            const errorMessage = err.message || 'Failed to delete note priority';
            setError(errorMessage);
            setErrorAlert(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [getBackendAccessToken, fetchPriorities, setInfoAlert, setErrorAlert]);

    // Check if name exists
    const checkNameExists = useCallback(async (name) => {
        try {
            const accessToken = await getBackendAccessToken();
            return await notePriorityService.checkNameExists(accessToken, name);
        } catch (err) {
            console.error('Error checking name exists:', err);
            return false;
        }
    }, [getBackendAccessToken]);

    // Check if weight exists
    const checkWeightExists = useCallback(async (weight) => {
        try {
            const accessToken = await getBackendAccessToken();
            return await notePriorityService.checkWeightExists(accessToken, weight);
        } catch (err) {
            console.error('Error checking weight exists:', err);
            return false;
        }
    }, [getBackendAccessToken]);

    // Get highest priority
    const getHighest = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const accessToken = await getBackendAccessToken();
            return await notePriorityService.getHighest(accessToken);
        } catch (err) {
            console.error('Error fetching highest priority:', err);
            const errorMessage = err.message || 'Failed to load highest priority';
            setError(errorMessage);
            setErrorAlert(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [getBackendAccessToken, setErrorAlert]);

    // Get lowest priority
    const getLowest = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const accessToken = await getBackendAccessToken();
            return await notePriorityService.getLowest(accessToken);
        } catch (err) {
            console.error('Error fetching lowest priority:', err);
            const errorMessage = err.message || 'Failed to load lowest priority';
            setError(errorMessage);
            setErrorAlert(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [getBackendAccessToken, setErrorAlert]);

    // Auto-fetch on mount
    useEffect(() => {
        if (autoFetch) {
            fetchPriorities();
        }
    }, [autoFetch, fetchPriorities]);

    return {
        priorities,
        loading,
        error,
        fetchPriorities,
        createPriority,
        updatePriority,
        deletePriority,
        checkNameExists,
        checkWeightExists,
        getHighest,
        getLowest,
        refetch: fetchPriorities
    };
};

export default useNotePriorities;
